import { axios, toQueryString } from '.'
import { useQuery, useMutation, UseQueryOptions} from '@tanstack/react-query'
import { HistoryDevice, HistoryPartGroups } from '../types'

const anyAPI = async () => await axios.main.get('anything').then((res) => res.data)
export const useAnyAPI = (options: object) => useQuery({ queryKey: ['anyAPI'], queryFn: anyAPI, ...options })

type ListParams = {
  pageSize?: number
  pageNo?: number
}
type ID = string | number
type DetailsParams = {
  id: ID
}
type CalcCreateParams = Record<string, any>
type UserParams = {
  partId?: number
  groupId?: number
  role?: string
  loginId: string
  userName?: string
  password?: string
  passwordCheck?: string
  userDesc?: string
}
type partParams = {
  partName: string
  partDesc: string
}
type groupParams = {
  groupName: string
  groupDesc: string
}

type deviceParams = {
  partId: number
  groupId: number
  driveNumber: string
  deviceAlias:string
}
type deviceUpdateParams = { 
  partId?: number, 
  groupId?: number,
  deviceAlias: string; 
  driveNumber: string; 
  deviceId : string; 
  linkedUserIds:string[] 
}

const getUserList = async (data: string) => await axios.main.get(`users/list${data}`).then((res) => res.data)
export const useGetUserList = (data: string, options?: object) => useQuery({ queryKey: ['useGetUserList', data], queryFn: () => getUserList(data), ...options, staleTime: 0, gcTime: 0  })

const getPartList = async (data: string) => await axios.main.get(`parts/list${data}`).then((res) => res.data)
export const useGetPartList = (data: string, options?: object) => useQuery({ queryKey: ['useGetPartList',data], queryFn: () => getPartList(data), ...options, staleTime: 0, gcTime: 0 }) //여기도 캐시지우는게 좋을수있음

const getGroupList = async (data: string) => await axios.main.get(`groups/list${data}`).then((res) => res.data)
export const useGetGroupList = (data: string, options?: object) => useQuery({ queryKey: ['useGetGroupList',data], queryFn: () => getGroupList(data), ...options, staleTime: 0, gcTime: 0 })

const getActivity = async (data: string) => await axios.main.get(`uaHistory${data}`).then((res) => res.data)
export const useGetActivity = (data: string, options?: object) => useQuery({ queryKey: ['getActivity',data], queryFn: () => getActivity(data), ...options, staleTime: 0, gcTime: 0 })

const getCalcList = async (data: ListParams) => await axios.main.get(`safe/calcuDateList?${toQueryString(data)}`).then((res) => res.data)
export const useGetCalcList = (data: ListParams, options?: object) => useQuery({ queryKey: ['usegetCalcList', data], queryFn: () => getCalcList(data), ...options, staleTime: 0, gcTime: 0  })

const getCalcDetails = async (data: DetailsParams) => await axios.main.get(`safe/detail/${data.id}`).then((res) => res.data)
export const useGetCalcDetails = (data: DetailsParams, options?: object) => useQuery({ queryKey: ['useGetCalcDetails', data.id], queryFn: () => getCalcDetails(data), ...options, enabled: !!data.id, staleTime: 0, gcTime: 0  })

const createCalc = async (data: CalcCreateParams) => await axios.main.post(`safe/insertCalcuDate`, data).then((res) => res.data)
export const useCreateCalc = (options?: object) => useMutation({ mutationFn: createCalc, ...options })

const updateCalc = async (data: CalcCreateParams) => await axios.main.post(`safe/updateCaleDate`, data).then((res) => res.data)
export const useUpdateCalc = (options?: object) => useMutation({ mutationFn: updateCalc, ...options })

const deleteCalc = async (data: DetailsParams) => await axios.main.delete(`safe/${data.id}`).then((res) => res.data)
export const useDeleteCalc = (options?: object) => useMutation({ mutationFn: deleteCalc, ...options })

const activateCalc = async (data: {id: ID}) => await axios.main.post(`safe/applyCalcu`, data).then(res => res.data)
export const useActivateCalc = (options?: object) => useMutation({mutationFn: activateCalc, ...options})

const getGroupByPart = async (partId: string) => await axios.main.get(`groups/byPart/${partId}`).then((res) => res.data)
export const useGetGroupByPart = (partId: string, options?: object) => useQuery({ queryKey: ['getGroupByPart', partId], queryFn: () => getGroupByPart(partId), ...options, staleTime: 0, gcTime: 0 })

const createUser = async (data: UserParams) => await axios.main.post(`users`, data).then((res) => res.data)
export const useCreateUser = (options?: object) => useMutation({ mutationFn: createUser, ...options })

const getPartsAll = async () => await axios.main.get(`parts/all`).then((res) => res.data)
export const useGetPartsAll = (options?: object) => useQuery({ queryKey: ['useGetPartsAll'], queryFn: () => getPartsAll(), ...options, staleTime: 0, gcTime: 0  })

const getAllPartAndGroup = async () => await axios.main.get(`groups/getAllPartAndGroup`).then((res) => res.data)
export const useGetAllPartAndGroup = (options?: object) => useQuery<HistoryPartGroups[]>({ queryKey: ['useGetAllPartAndGroup'], queryFn: () => getAllPartAndGroup(), ...options, staleTime: 0, gcTime: 0 })

const getGroupDeviceList = async (groupId: ID) => await axios.main.get(`devices/deviceList/${groupId}`).then((res) => res.data)
export const useGetGroupDeviceList = (groupId: ID, options?: object) => useQuery<HistoryDevice[]>({ queryKey: ['useGetGroupDeviceList', groupId], queryFn: () => getGroupDeviceList(groupId), ...options, enabled: Number(groupId) > 0, staleTime: 0, gcTime: 0})

const getUserDetail = async (data: string) => await axios.main.get(`users/${data}`).then((res) => res.data)
export const useGetUserDetail = (data: string, options?: object) => useQuery({ queryKey: ['getUserDetail', data], queryFn: () => getUserDetail(data), ...options, staleTime: 0, gcTime: 0  })

const updateUser = async ({loginId,...rest }:UserParams & {loginId:string}) => await axios.main.patch(`users/${loginId}`, rest).then((res) => res.data)
export const useUpdateUser = (options?: object) => useMutation({ mutationFn: updateUser, ...options })

const deleteUser = async (Id: string) => await axios.main.delete(`users/${Id}`).then((res) => res.data)
export const useDeleteUser = (options?: object) => useMutation({ mutationFn: deleteUser, ...options })

const getSearchRoles = async () => await axios.main.get(`users/roles/search`).then((res) => res.data)
export const useGetSearchRoles = (options?: object) => useQuery({ queryKey: ['getSearchRoles'], queryFn: () => getSearchRoles(), ...options, staleTime: 0, gcTime: 0  })

const getRegisterRoles = async () => await axios.main.get(`users/roles/register`).then((res) => res.data)
export const useGetRegisterRoles = (options?: object) => useQuery({ queryKey: ['getRegisterRoles'], queryFn: () => getRegisterRoles(), ...options, staleTime: 0, gcTime: 0  })

const createPart = async (data: partParams) => await axios.main.post(`parts`, data).then((res) => res.data)
export const useCreatePart = (options?: object) => useMutation({ mutationFn: createPart, ...options })

const getPartDetail = async (data: string) => await axios.main.get(`parts/${data}`).then((res) => res.data)
export const useGetPartDetail = (data: string, options?: object) => useQuery({ queryKey: ['getPartDetail', data], queryFn: () => getPartDetail(data), ...options, staleTime: 0, gcTime: 0  })

const updatePart = async ({partId,...rest}: partParams & {partId: string}) => await axios.main.patch(`parts/${partId}`, rest).then((res) => res.data)
export const useUpdatePart = (options?: object) => useMutation({ mutationFn: updatePart, ...options })

const deletePart = async (Id: string) => await axios.main.delete(`parts/${Id}`).then((res) => res.data)
export const useDeletePart = (options?: object) => useMutation({ mutationFn: deletePart, ...options })

const createGroup = async (data: groupParams & {partId: string}) => await axios.main.post(`groups`, data).then((res) => res.data)
export const useCreateGroup = (options?: object) => useMutation({ mutationFn: createGroup, ...options })

const getGroupDetail = async (data: string) => await axios.main.get(`groups/${data}`).then((res) => res.data)
export const useGetGroupDetail = (data: string, options?: object) => useQuery({ queryKey: ['getGroupDetail', data], queryFn: () => getGroupDetail(data), ...options, staleTime: 0, gcTime: 0  })

const updateGroup = async ({groupId, ...rest}: groupParams & {groupId: string}) => await axios.main.patch(`groups/${groupId}`, rest).then((res) => res.data)
export const useUpdateGroup = (options?: object) => useMutation({ mutationFn: updateGroup, ...options })

const deleteGroup = async (Id: string) => await axios.main.delete(`groups/${Id}`).then((res) => res.data)
export const useDeleteGroup = (options?: object) => useMutation({ mutationFn: deleteGroup, ...options })

const getDeviceList = async (data: string) => await axios.main.get(`devices/list${data}`).then((res) => res.data)
export const useGetDeviceList = (data: string, options?: object) => useQuery({ queryKey: ['getDeviceList',data], queryFn: () => getDeviceList(data), ...options, staleTime: 0, gcTime: 0 })

const createDevice = async (data: deviceParams) => await axios.main.post(`devices`, data).then((res) => res.data)
export const useCreateDevice = (options?: object) => useMutation({ mutationFn: createDevice, ...options })

const getDeviceDetail = async (data: string) => await axios.main.get(`devices/${data}`).then((res) => res.data)
export const useGetDeviceDetail = (data: string, options?: object) => useQuery({ queryKey: ['getDeviceDetail', data], queryFn: () => getDeviceDetail(data), ...options, staleTime: 0, gcTime: 0  })

const updateDevice = async ({deviceId, ...rest}: deviceUpdateParams) => await axios.main.patch(`devices/${deviceId}`, rest).then((res) => res.data)
export const useUpdateDevice = (options?: object) => useMutation({ mutationFn: updateDevice, ...options })

const deleteDevice = async (Id: string) => await axios.main.delete(`devices/${Id}`).then((res) => res.data)
export const useDeleteDevice = (options?: object) => useMutation({ mutationFn: deleteDevice, ...options })

const linkDevice = async (params: { driveNumber: string;}) => await axios.main.post(`devices/link`, params).then((res) => res.data)
export const useLinkDevice = (options?: object) => useMutation({ mutationFn: linkDevice, ...options })

const notifyMobile = async (data: { mobileId: string}) => await axios.main.post(`push/notification/upload`, data).then((res) => res.data)
export const useNotifyMobile = (options?: object) => useMutation({ mutationFn: notifyMobile, ...options })

const getActivityExcel = async (data: string) => await axios.main.get(`uaHistory/excel${data}`,{ headers: { "Content-Type": "blob" }, responseType: "arraybuffer" }).then((res) => res.data)
export const useGetActivityExcel = (data: string, options?: object) => useQuery({ queryKey: ['getActivityExcel',data], queryFn: () => getActivityExcel(data), ...options, staleTime: 0, gcTime: 0 })

const getUploadList = async (data: string) => await axios.main.get(`devices/uploadHistory${data}`).then((res) => res.data)
export const useGetUploadList = (data: string, options?: object) => useQuery({ queryKey: ['getUploadList',data], queryFn: () => getUploadList(data), ...options, staleTime: 0, gcTime: 0 })